// Core Libraries
import React, { useEffect, useState } from 'react';
import { NextPage } from 'next';
import Link from 'next/link';

// Store, Components
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import Slider from '@/components/Slider';
import BubbleContainer from '@/components/BubbleContainer';
import Campaings from '@/components/Campaings';
import Spinner from '@/components/Spinner';
import Modal from '@/components/Modal';
import BrandingStripe from '@cosmos/branding-stripe';

import useWindowSize, { Size } from '@/utils/hooks/useWindowSize';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { AppState } from '@/store/store';
import {
  campaignSelectors,
  fetchBanners,
  fetchCampaigns,
  fetchCities,
} from '@/store/reducers/Campaign/campaignSlice';
import { createPermission } from '@/store/reducers/Auth/authSlice';

import { homePageTexts, homePopUpTexts, routeTexts } from '@/assets/strings';
import { homeBannerSize } from '@/constants/imageSizes';

import HBBusService from 'src/services/hbBusService';
import AdBanner from '@/components/AdBanner';

const Home: NextPage = () => {
  const hbBus = HBBusService();
  const dispatch = useDispatch();
  const size: Size = useWindowSize();

  const { loading, error, cities, banners, campaignsLoading } = useSelector(
    (state: AppState) => state.campaign
  );
  const { gdprApprove, checkingUser } = useSelector(
    (state: AppState) => state.auth
  );

  const allCampaigns = useSelector(campaignSelectors.selectAll, shallowEqual);

  const [isModalOpen, setIsModelOpen] = useState<boolean>(false);

  const allCampaignsButtonHandler = () => {
    hbBus.allCampaignsButtonEvent();
  };

  useEffect(() => {
    async function fetchData() {
      await dispatch(fetchBanners());
      await dispatch(fetchCampaigns());
      await dispatch(fetchCities());
    }
    fetchData();
    if (!gdprApprove) setIsModelOpen(true);
  }, [gdprApprove]);

  const setIsModelOpenHandler = () => {
    setIsModelOpen(false);
    dispatch(createPermission());
  };
  return (
    <>
      {size.width !== undefined &&
        (size.width > 768 ? (
          <AdBanner type={'desktop'} template={'header'} />
        ) : (
          <AdBanner type={'mobile'} size={'small'} template={'header'} />
        ))}

      <Header />
      <div className='max-w-8xl mx-auto'>
        <BrandingStripe />
      </div>

      <div className='min-h-[292px] bg-gradient-to-b from-[#57BFFF] to-[#0382D0] flex flex-col items-center justify-between max-w-8xl mx-auto'>
        <div className='flex flex-col items-center mt-10'>
          <h1 className='font-[600] text-[22px] sm:text-[48px] text-white text-center'>
            Ayrıcalıklı ve avantajlı hizmetler dünyası
          </h1>
          <p className='font-[400] text-[16px] sm:text-[18px] text-white max-w-[600px] text-center'>
            Hepsiburada Çözüm Ortağı, Hepsiburada satıcılarına özel operasyonel,
            dijital, finansal, sosyal ve ticari ihtiyaçlarına yönelik tüm ürün
            ve hizmetlerin ayrıcalıklarla sunulduğu bir avantajlar dünyasıdır.
          </p>
        </div>
        <div className='mt-5 w-full sm:max-w-screen-xl mx-auto sm:px-0'>
          <div className='flex justify-evenly items-center bg-[#FF6000] p-3 text-[18px] font-bold text-white rounded-t-lg'>
            {size.width > 768 ? (
              <>
                <Link href={routeTexts.campaigns}>
                  <a>Operasyonel </a>
                </Link>
                <div className='h-7 bg-white w-[2px]'></div>
                <Link href={routeTexts.campaigns}>
                  <a>Dijital </a>
                </Link>
                <div className='h-7 bg-white w-[2px]'></div>
                <Link href={routeTexts.campaigns}>
                  <a>Finansal </a>
                </Link>
                <div className='h-7 bg-white w-[2px]'></div>
                <Link href={routeTexts.campaigns}>
                  <a>Sosyal </a>
                </Link>
                <div className='h-7 bg-white w-[2px]'></div>
                <Link href={routeTexts.campaigns}>
                  <a>Ticari </a>
                </Link>
              </>
            ) : (
              <Link href={routeTexts.campaigns}>
                <a>Hizmetler</a>
              </Link>
            )}
          </div>
        </div>
      </div>
      <div className='bg-[#F5F5F5] max-w-8xl mx-auto'>
        <div className='flex flex-col justify-start w-full sm:max-w-screen-xl mx-auto sm:px-0'>
          {!checkingUser && !gdprApprove && (
            <Modal isOpen={isModalOpen} onClose={setIsModelOpenHandler}>
              <div>
                {homePopUpTexts.main}
                <Link href={routeTexts.gdpr}>
                  <a target='_blank'>
                    <i>
                      <b>{homePopUpTexts.bold}</b>
                    </i>
                  </a>
                </Link>
                {homePopUpTexts.preview}
              </div>
            </Modal>
          )}
          {!(banners?.length > 0) ? null : (
            <div className=''>
              <Slider
                images={banners}
                size={homeBannerSize}
                location={'home'}
              />
            </div>
          )}
          <div className=' my-5 ml-8 lg:ml-0 lg:my-15'>
            <BubbleContainer />
          </div>
        </div>
      </div>
      <div className='rounded-lg mb-10'>
        {loading && !(cities?.length > 0) ? (
          <Spinner />
        ) : !allCampaigns?.length && error ? (
          <div className='text-2xl text-center text-orange mt-8'>
            {homePageTexts.noData}
          </div>
        ) : (
          <>
            {!campaignsLoading ? (
              <>
                {size.width !== undefined &&
                  (size.width > 768 ? (
                    <AdBanner type={'desktop'} template={'middle'} />
                  ) : (
                    <AdBanner type={'mobile'} template={'middle'} />
                  ))}

                <div className='flex flex-col items-center w-full sm:max-w-[900px] mx-auto sm:px-0'>
                  <Campaings
                    data={allCampaigns}
                    title={
                      <div className='text-[22px] md:text-[28px] text-[#484848] font-[700] mt-10 mb-2'>
                        Hizmetler{' '}
                        <Link href={routeTexts.campaigns}>
                          <a
                            className='text-[16px] text-[#FF6000] font-[600] ml-2'
                            onClick={allCampaignsButtonHandler}
                          >
                            Tümü {'>'}
                          </a>
                        </Link>
                      </div>
                    }
                  />
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
      {!campaignsLoading ? (
        <>
          <div className='mb-[50px]'>
            {size.width !== undefined &&
              (size.width > 768 ? (
                <AdBanner type={'desktop'} template={'footer'} />
              ) : (
                <AdBanner type={'mobile'} template={'footer'} />
              ))}
          </div>
          <Footer />
        </>
      ) : null}
    </>
  );
};

export default Home;
